

interface LevelRingProps {
  level: number
  progressPercent: number
  size?: 'sm' | 'md' | 'lg' | 'xl' // sm: w-10 (header), md: w-16, lg: w-24 (profile), xl: w-32 (profile md)
  avatarUrl?: string | null
  fallbackChar?: string
  theme?: 'dark' | 'light'
}

export default function LevelRing({
  level,
  progressPercent,
  size = 'lg',
  avatarUrl,
  fallbackChar = 'U',
  theme = 'dark'
}: LevelRingProps) {
  // Map size keys to pixels
  const sizeMap = {
    sm: { box: 44, avatar: 34, stroke: 2.5, badgeText: 'text-[9px]', badgeBox: 'w-4 h-4 -bottom-1 -right-1', levelPrefix: '' },
    md: { box: 68, avatar: 56, stroke: 3, badgeText: 'text-[10px]', badgeBox: 'w-5 h-5 -bottom-1 -right-1', levelPrefix: 'L' },
    lg: { box: 104, avatar: 88, stroke: 4, badgeText: 'text-[11px]', badgeBox: 'w-6 h-6 -bottom-1 -right-1', levelPrefix: 'Lv.' },
    xl: { box: 136, avatar: 116, stroke: 5, badgeText: 'text-[12px]', badgeBox: 'w-7 h-7 -bottom-1 -right-1', levelPrefix: 'Lv.' }
  }

  const currentSize = sizeMap[size]
  const radius = (currentSize.box - currentSize.stroke) / 2
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (progressPercent / 100) * circumference

  return (
    <div 
      className="relative flex items-center justify-center"
      style={{ width: currentSize.box, height: currentSize.box }}
    >
      {/* SVG EXP Level Progress Ring */}
      <svg 
        className="absolute transform -rotate-90 pointer-events-none"
        width={currentSize.box} 
        height={currentSize.box}
      >
        {/* Track Ring */}
        <circle
          cx={currentSize.box / 2}
          cy={currentSize.box / 2}
          r={radius}
          fill="transparent"
          stroke={theme === 'dark' ? '#1f2937' : '#e5e7eb'}
          strokeWidth={currentSize.stroke}
        />
        {/* Progress Ring (Gradient Gold) */}
        <circle
          cx={currentSize.box / 2}
          cy={currentSize.box / 2}
          r={radius}
          fill="transparent"
          stroke="url(#levelRingGoldGradient)"
          strokeWidth={currentSize.stroke}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-700 ease-out"
        />
        
        {/* Defs for Gradient */}
        <defs>
          <linearGradient id="levelRingGoldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f59e0b" /> {/* amber-500 */}
            <stop offset="50%" stopColor="#d97706" /> {/* amber-600 */}
            <stop offset="100%" stopColor="#b45309" /> {/* amber-700 */}
          </linearGradient>
        </defs>
      </svg>

      {/* Inner Avatar Container */}
      <div 
        className={`rounded-full overflow-hidden flex items-center justify-center relative ${
          theme === 'dark' ? 'bg-black' : 'bg-white'
        }`}
        style={{ 
          width: currentSize.avatar, 
          height: currentSize.avatar,
        }}
      >
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt="User Avatar"
            className="w-full h-full object-cover rounded-full select-none"
          />
        ) : (
          <span className={`font-bold select-none ${
            size === 'sm' ? 'text-sm' : size === 'md' ? 'text-lg' : size === 'lg' ? 'text-3xl' : 'text-4xl'
          } ${
            theme === 'dark' ? 'text-gold-400' : 'text-gold-500'
          }`}>
            {fallbackChar.charAt(0).toUpperCase()}
          </span>
        )}
      </div>

      {/* Level Badge Overlay */}
      <div 
        className={`absolute rounded-full flex items-center justify-center font-extrabold shadow-md border ${
          theme === 'dark' 
            ? 'bg-gradient-to-r from-yellow-500 to-amber-600 border-black text-black' 
            : 'bg-gradient-to-r from-yellow-500 to-amber-600 border-white text-white'
        } ${currentSize.badgeBox}`}
      >
        <span className={`${currentSize.badgeText} leading-none tracking-tighter`}>
          {currentSize.levelPrefix}{level}
        </span>
      </div>
    </div>
  )
}
